// Maximum font file size (20MB)
const MAX_FONT_SIZE = 20 * 1024 * 1024

/**
 * Convert a File to base64 string
 * Includes validation for proper data URL format and size limits
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    // Validate file size
    if (file.size > MAX_FONT_SIZE) {
      reject(new Error(`Font file too large. Maximum size is ${MAX_FONT_SIZE / 1024 / 1024}MB`))
      return
    }

    if (file.size === 0) {
      reject(new Error('Font file is empty'))
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string

      // Validate data URL format
      if (!result || typeof result !== 'string') {
        reject(new Error('Failed to read file: invalid result'))
        return
      }

      if (!result.includes(',')) {
        reject(new Error('Invalid data URL format: missing comma separator'))
        return
      }

      // Remove the data URL prefix (e.g., "data:font/ttf;base64,")
      const base64 = result.split(',')[1]

      if (!base64 || base64.length === 0) {
        reject(new Error('Empty base64 data after conversion'))
        return
      }

      // Basic base64 validation (check for valid characters)
      const base64Regex = /^[A-Za-z0-9+/]+=*$/
      if (!base64Regex.test(base64)) {
        reject(new Error('Invalid base64 encoding'))
        return
      }

      resolve(base64)
    }
    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${reader.error?.message || 'Unknown error'}`))
    }
    reader.readAsDataURL(file)
  })
}

/**
 * Convert base64 to data URL for font loading
 */
export function base64ToDataUrl(base64: string, format: string): string {
  const mimeType = getMimeType(format)
  return `data:${mimeType};base64,${base64}`
}

/**
 * Get MIME type from font format
 */
function getMimeType(format: string): string {
  const mimeTypes: Record<string, string> = {
    ttf: 'font/ttf',
    otf: 'font/otf',
    woff: 'font/woff',
    woff2: 'font/woff2',
  }
  return mimeTypes[format.toLowerCase()] || 'font/ttf'
}

/**
 * Get font format from file extension
 */
export function getFontFormat(filename: string): 'ttf' | 'otf' | 'woff' | 'woff2' {
  const ext = filename.split('.').pop()?.toLowerCase()
  if (ext === 'ttf' || ext === 'otf' || ext === 'woff' || ext === 'woff2') {
    return ext
  }
  return 'ttf' // Default
}

/**
 * Validate if file is a valid font file
 */
export function isValidFontFile(file: File): boolean {
  const validExtensions = ['.ttf', '.otf', '.woff', '.woff2']
  const extension = '.' + file.name.split('.').pop()?.toLowerCase()
  return validExtensions.includes(extension)
}
